import React, { useState, useEffect, useRef, useCallback } from 'react';
import { UnifiedHeader as StyledUnifiedHeader, TripSection, TripDropdown, ViewToggle, ViewButton } from '../../styled';
import { MActionItemsList } from '../MActionItemsList';
import * as Styled from './UnifiedHeader.styles';
import clsx from 'clsx';
import { useUIState } from '../../store';

const SIDEBAR_NAV_ID = 'chat-sidebar-navigation';

interface UnifiedHeaderProps {
  currentChatName: string;
  currentChatId?: string | null;
  onToggleSidebar: () => void;
  menuOnly?: boolean;
  showChat?: boolean;
  onViewToggle?: () => void;
  onRenameChat?: (chatId: string, newTitle: string) => void;
  onDeleteChat?: (chatId: string) => void;
  onNewChat?: () => void;
}

export const UnifiedHeader: React.FC<UnifiedHeaderProps> = ({
  currentChatName,
  currentChatId,
  onToggleSidebar,
  menuOnly = false,
  showChat = true,
  onViewToggle,
  onNewChat,
  onRenameChat,
  onDeleteChat,
}) => {
  const { isSidebarOpen } = useUIState();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');

  const handleSaveRename = useCallback(() => {
    if (onRenameChat && renameValue.trim() && currentChatId) {
      onRenameChat(currentChatId, renameValue.trim());
    }
    setIsRenaming(false);
    setRenameValue('');
    setShowMenu(false);
  }, [onRenameChat, renameValue, currentChatId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || dropdownRef.current?.contains(target)) {
        return;
      }
      setShowMenu(false);
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  useEffect(() => {
    const handleRenameClickOutside = (event: MouseEvent) => {
      if (renameInputRef.current && !renameInputRef.current.contains(event.target as Node)) {
        handleSaveRename();
      }
    };

    if (isRenaming) {
      document.addEventListener('mousedown', handleRenameClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleRenameClickOutside);
    };
  }, [isRenaming, handleSaveRename]);

  const handleMenuClick = () => {
    setShowMenu(prev => !prev);
  };

  const handleRename = () => {
    setIsRenaming(true);
    setRenameValue(currentChatName);
    setShowMenu(false);
  };

  const handleCancelRename = () => {
    setIsRenaming(false);
    setRenameValue('');
    setShowMenu(false);
  };

  const handleDelete = () => {
    if (onDeleteChat && currentChatId) {
      onDeleteChat(currentChatId);
    }
    setShowMenu(false);
  };

  function handleNewChat(): void {
    if (onNewChat) onNewChat();
    setShowMenu(false);
  }

  return (
    <StyledUnifiedHeader>
      <Styled.MenuButton
        type="button"
        aria-label={isSidebarOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={isSidebarOpen}
        aria-controls={SIDEBAR_NAV_ID}
        onClick={e => {
          e.stopPropagation();
          onToggleSidebar();
        }}
      >
        <span className="icon-menu icon-m" aria-hidden="true" />
      </Styled.MenuButton>
      {!menuOnly && (
        <TripSection>
          <Styled.ChatNameControl>
            {isRenaming ? (
              <Styled.RenameInputWrapper
                className={clsx('t-font-s', 'text-truncate mb-0')}
                onClick={e => e.stopPropagation()}
              >
                <Styled.RenameInput
                  ref={renameInputRef}
                  type="text"
                  aria-label="Chat name"
                  value={renameValue}
                  onChange={e => setRenameValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleSaveRename();
                    if (e.key === 'Escape') handleCancelRename();
                    e.stopPropagation();
                  }}
                  onClick={e => e.stopPropagation()}
                />
              </Styled.RenameInputWrapper>
            ) : (
              <>
                <TripDropdown
                  ref={menuRef}
                  type="button"
                  aria-label={currentChatName}
                  aria-expanded={showMenu}
                  aria-haspopup="menu"
                  onClick={handleMenuClick}
                >
                  <Styled.Text className={clsx('t-font-s', 'text-truncate mb-0')}>{currentChatName}</Styled.Text>
                  <span className="icon-dropdown-down" aria-hidden="true" />
                </TripDropdown>
                {showMenu && (
                  <div ref={dropdownRef}>
                    <MActionItemsList
                      activeDropdown={showMenu}
                      onRename={handleRename}
                      onDelete={handleDelete}
                      onNewChat={handleNewChat}
                      component="header"
                    />
                  </div>
                )}
              </>
            )}
          </Styled.ChatNameControl>
        </TripSection>
      )}

      {!menuOnly && onViewToggle && (
        <ViewToggle>
          <ViewButton active={showChat} onClick={() => !showChat && onViewToggle()}>
            Chat
          </ViewButton>
          <ViewButton active={!showChat} onClick={() => showChat && onViewToggle()}>
            Results
          </ViewButton>
        </ViewToggle>
      )}
    </StyledUnifiedHeader>
  );
};
